/**
 * Normalizes optional delta fields from GET /home/assets-monitoring (or mock).
 * Backend may send flat keys (`critical_delta`) or nested `deltas: { critical, ... }`.
 */

export type AssetsMonitoringSeverity = 'critical' | 'high' | 'medium' | 'low'

export function getSeverityDelta(
  raw: Record<string, unknown> | null | undefined,
  severity: AssetsMonitoringSeverity
): number {
  if (!raw || typeof raw !== 'object') return 0
  const flat = (raw as Record<string, unknown>)[`${severity}_delta`]
  if (Number.isFinite(Number(flat))) return Number(flat)
  const deltas = (raw as Record<string, unknown>).deltas
  if (deltas && typeof deltas === 'object') {
    const v = (deltas as Record<string, unknown>)[severity]
    if (Number.isFinite(Number(v))) return Number(v)
  }
  return 0
}

export function sumProblemClassCounts(raw: Record<string, unknown> | null | undefined): number {
  if (!raw || typeof raw !== 'object') return 0
  const problems = (raw as Record<string, unknown>).problems
  if (problems && typeof problems === 'object') {
    const total = Number((problems as Record<string, unknown>).total)
    if (Number.isFinite(total)) return total
  }
  const c = ['critical', 'high', 'medium', 'low'] as const
  return c.reduce((acc, k) => acc + (Number.isFinite(Number((raw as Record<string, unknown>)[k])) ? Number((raw as Record<string, unknown>)[k]) : 0), 0)
}

export type HomeProblemsBreakdown = {
  total: number
  vulnerabilities: number
  risks: number
  controls: number
}

export function getProblemsBreakdown(
  raw: Record<string, unknown> | null | undefined
): HomeProblemsBreakdown {
  const empty = { total: 0, vulnerabilities: 0, risks: 0, controls: 0 }
  if (!raw || typeof raw !== 'object') return empty
  const problems = (raw as Record<string, unknown>).problems
  if (problems && typeof problems === 'object') {
    const p = problems as Record<string, unknown>
    return {
      total: Number(p.total) || 0,
      vulnerabilities: Number(p.vulnerabilities) || 0,
      risks: Number(p.risks) || 0,
      controls: Number(p.controls) || 0,
    }
  }
  const vulns = sumProblemClassCounts(raw)
  return { total: vulns, vulnerabilities: vulns, risks: 0, controls: 0 }
}

export function formatSignedDelta(n: number): string {
  if (!Number.isFinite(n)) return '0'
  const v = Math.trunc(n)
  return v > 0 ? `+${v}` : String(v)
}
